import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import {
  LayoutDashboard,
  Users,
  MapPin,
  Calendar,
  FileText,
  LogOut,
  Menu,
  X,
  MonitorPlay,
  TreePalm,
  BarChart3,
  Wallet,
  Upload,
  UserCircle,
  PieChart,
  FileUp,
  ClipboardList,
  ClipboardCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';

const adminNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/employees', label: 'Employees', icon: Users },
  { path: '/locations', label: 'Locations', icon: MapPin },
  { path: '/attendance', label: 'Attendance', icon: Calendar },
  { path: '/summary', label: 'Summary', icon: FileText },
  { path: '/leaves', label: 'Leave Mgmt', icon: TreePalm },
  { path: '/task-master', label: 'Task Master', icon: ClipboardList },
  { path: '/task-assignment', label: 'Task Assign', icon: ClipboardCheck },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/payroll', label: 'Payroll', icon: Wallet },
  { path: '/analytics', label: 'Analytics', icon: PieChart },
  { path: '/payslip-upload', label: 'Upload Payslips', icon: FileUp },
  { path: '/bulk-import', label: 'Bulk Import', icon: Upload },
];

const employeeNavItems = [
  { path: '/my-dashboard', label: 'My Dashboard', icon: UserCircle },
  { path: '/my-tasks', label: 'My Tasks', icon: ClipboardCheck },
  { path: '/leaves', label: 'My Leaves', icon: TreePalm },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = isAdmin ? adminNavItems : employeeNavItems;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:w-64 lg:flex-col">
        <div className="flex flex-col h-full bg-white border-r border-slate-200 shadow-sm">
          {/* Logo */}
          <div className="flex items-center justify-center px-6 py-4 border-b border-slate-100 shrink-0">
            <img 
              src="/seeza-logo.png" 
              alt="Seeza Trading Company" 
              className="h-14 w-auto object-contain"
            />
          </div>

          {/* Navigation — scrollable */}
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-[#FF9933] text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}

            {/* Kiosk Mode Button */}
            {isAdmin && (
              <NavLink
                to="/kiosk"
                target="_blank"
                className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-[#138808] hover:bg-green-50 transition-all duration-200 mt-4"
                data-testid="nav-kiosk"
              >
                <MonitorPlay className="w-5 h-5" />
                Open Kiosk Mode
              </NavLink>
            )}
          </nav>

          {/* User Info — fixed at bottom */}
          <div className="px-4 py-4 border-t border-slate-100 shrink-0">
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50">
              <div className="w-9 h-9 rounded-full bg-[#FF9933] flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50">
        <div className="flex items-center justify-between px-4 py-3">
          <img 
            src="/seeza-logo.png" 
            alt="Seeza Trading" 
            className="h-10 w-auto object-contain"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="bg-white border-t border-slate-100 px-4 py-4 shadow-lg">
            <nav className="space-y-1">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium ${
                      isActive
                        ? 'bg-[#FF9933] text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to="/kiosk"
                  target="_blank"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-[#138808] hover:bg-green-50"
                >
                  <MonitorPlay className="w-5 h-5" />
                  Open Kiosk Mode
                </NavLink>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 mt-4"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8 min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
